// @givmo/org-console — the admin's OWN preferences (shell contract v3, T2).
//
// ══ THE PAGE HARDCODES NO PREFERENCE ═══════════════════════════════════════════════════════════
//
// The server serves the DEFINITIONS — module-declared, class availability already applied, the
// caller's audience already filtered — beside the caller's stored VALUES, and this renders exactly
// what was served: a definition the backend does not serve is a control that does not exist. A new
// preference is a definition, never a client release.
//
// Two anchors at birth (T2.6): LANDING — the one key whose control is a DROPDOWN whose first option
// reads "Default" (ruled 2026-09-02: never "Let Givmo decide"; the radio-tile list is rejected),
// followed by the apps the person can OPEN (host-supplied — the openable set is the host's
// derivation); and NOTIFICATIONS — every other definition, a switch for a boolean and a dropdown
// for a choice, grouped per module. Rows become pages if a module's group outgrows a section.
//
// ⚠ A STALE STORED CHOICE IS SHOWN AS SELECTED, WITH ITS NOTE — never as "Default", never dropped.
// The stored value is what the person chose; the landing rule falls through past it while it is
// unopenable, and the page says so rather than pretending the choice is gone.
//
// ⚠ THE LANDING KEY NEVER LANDS ANYONE FROM HERE. The root reads `default_landing_module` off the
// ONE standing read; this page's read is the preferences GET only, and a write here invalidates the
// standing read so the root learns the new choice.
//
// The write is PARTIAL and THREE-VALUED per key: choosing "Default" sends `null` (reset — the row
// is deleted); anything else sends the value. Never the whole form.
//
// DATA CONTRACT: the `org-preferences-*` test-id family.

import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorMessage,
  Select,
  Switch,
  testIdProp,
  type SelectOption,
} from '@givmo/design-system';
import type { OrgConsoleConfig } from './org-console-config';
import { isOrgConsoleUnavailable } from './org-console-transport';
import { titleizeSlug } from './org-console-config';
import { OrgConsoleUnavailableNotice } from './org-console-notices';
import { useOrgAdminPreferences, useUpdateOrgAdminPreferences } from './use-org-console';
import type { OrgAdminPreferenceDefinition } from './wire-types';

export const ORG_PREFERENCES_TEST_ID = 'org-preferences';
/** The one key the landing rule consumes. Its definition is served by the server like any other. */
export const LANDING_PREFERENCE_KEY = 'landing.default_module';
/** The dropdown's first option — pinned copy (ruled 2026-09-02). */
export const LANDING_DEFAULT_OPTION_LABEL = 'Default';
const LANDING_DEFAULT_VALUE = '__default__';

export interface LandingOption {
  /** A module slug the person can open. */
  value: string;
  label: string;
}

export interface OrgAdminPreferencesPanelProps {
  config: OrgConsoleConfig;
  /**
   * The apps this person can OPEN, in display order — the host's derivation (`openableSquares`).
   * The Admin Console tile is never among them (R1).
   */
  landingOptions: readonly LandingOption[];
  /**
   * What "Default" resolves to RIGHT NOW — the landing rule's answer with no chosen default, as a
   * label ("Donations"). Absent ⇒ the hint line is omitted.
   */
  defaultResolvesTo?: string | null;
  /** The host's page already carries this title: render it for assistive technology alone. */
  headingHidden?: boolean;
}

/** A choice's display word — the server's value titleized ("email" → "Email"). */
function choiceLabel(value: string): string {
  return titleizeSlug(value);
}

export function OrgAdminPreferencesPanel({
  config,
  landingOptions,
  defaultResolvesTo,
  headingHidden = false,
}: OrgAdminPreferencesPanelProps) {
  const prefix = ORG_PREFERENCES_TEST_ID;
  const read = useOrgAdminPreferences(config.orgRef);
  const update = useUpdateOrgAdminPreferences(config.orgRef, config.onPreferencesWritten);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const definitions = useMemo(() => read.data?.definitions ?? [], [read.data]);
  const values = useMemo(() => read.data?.values ?? {}, [read.data]);
  const landing = definitions.find((definition) => definition.key === LANDING_PREFERENCE_KEY);
  const others = definitions.filter((definition) => definition.key !== LANDING_PREFERENCE_KEY);

  const write = (key: string, value: unknown) => {
    setSavingKey(key);
    setSaved(false);
    update.mutate(
      { values: { [key]: value } },
      {
        onSuccess: () => setSaved(true),
        onSettled: () => setSavingKey(null),
      },
    );
  };

  // ── The landing dropdown ───────────────────────────────────────────────────────────────────
  const stored = values[LANDING_PREFERENCE_KEY];
  const storedSlug = typeof stored === 'string' && stored.trim().length > 0 ? stored.trim() : null;
  const storedOption =
    storedSlug === null ? undefined : landingOptions.find((o) => o.value === storedSlug);
  const stale = storedSlug !== null && storedOption === undefined;
  const staleLabel = storedSlug === null ? '' : config.vocab.moduleLabel(storedSlug);
  const landingSelectOptions: SelectOption[] = [
    { value: LANDING_DEFAULT_VALUE, label: LANDING_DEFAULT_OPTION_LABEL },
    ...landingOptions.map((option) => ({ value: option.value, label: option.label })),
    // The stale choice stays SELECTABLE-as-selected: it is listed so the control can show it,
    // and it renders with its note rather than silently reading "Default".
    ...(stale ? [{ value: storedSlug as string, label: staleLabel }] : []),
  ];
  const landingValue = storedSlug ?? LANDING_DEFAULT_VALUE;
  const resolves = (defaultResolvesTo ?? '').trim();
  const landingHint = stale
    ? `${staleLabel} is not available right now. Until it is, you land on the default${resolves.length > 0 ? ` — ${resolves}` : ''}.`
    : storedSlug === null
      ? resolves.length > 0
        ? `Right now that is ${resolves}.`
        : ''
      : `Givmo opens in ${storedOption?.label ?? staleLabel} when you sign in.`;

  // ── The other definitions, grouped per module ──────────────────────────────────────────────
  const groups = useMemo(() => {
    const byModule = new Map<string, OrgAdminPreferenceDefinition[]>();
    for (const definition of others) {
      const list = byModule.get(definition.module) ?? [];
      list.push(definition);
      byModule.set(definition.module, list);
    }
    return [...byModule.entries()];
  }, [others]);

  if (read.isError && isOrgConsoleUnavailable(read.error)) {
    return (
      <OrgConsoleUnavailableNotice
        testId={`${prefix}-unavailable`}
        title="Preferences"
        description="This environment doesn’t have the console turned on, so there are no preferences to show yet."
      />
    );
  }

  return (
    <section className="flex flex-col gap-16" {...testIdProp(prefix)}>
      <h2
        className={
          headingHidden ? 'sr-only' : 'font-sans text-s16 font-w700 tracking-givmo text-text-strong'
        }
      >
        Preferences
      </h2>

      {/* The live region every save reports into — loading, saved, or the refusal. */}
      <p role="status" aria-live="polite" className="sr-only" {...testIdProp(`${prefix}-status`)}>
        {read.isPending
          ? 'Loading your preferences…'
          : savingKey !== null
            ? 'Saving…'
            : saved
              ? 'Saved.'
              : ''}
      </p>

      {read.isPending && (
        <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${prefix}-loading`)}>
          Loading your preferences…
        </p>
      )}

      {read.isError && !isOrgConsoleUnavailable(read.error) && (
        <div className="flex flex-col items-start gap-8">
          <ErrorMessage testId={`${prefix}-error`}>We couldn’t load your preferences.</ErrorMessage>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void read.refetch()}
            testId={`${prefix}-retry`}
          >
            Try again
          </Button>
        </div>
      )}

      {landing !== undefined && (
        <Card id="landing" testId={`${prefix}-landing`}>
          <CardHeader
            title="Landing"
            subtitle="Where Givmo opens when you sign in, and where the Givmo mark takes you."
          />
          <CardBody>
            <div className="flex max-w-[420px] flex-col gap-8">
              <Select
                appearance="saas"
                label="Open Givmo in"
                options={landingSelectOptions}
                value={landingValue}
                disabled={savingKey === LANDING_PREFERENCE_KEY}
                onValueChange={(value) => {
                  if (value === null || value === landingValue) return;
                  write(LANDING_PREFERENCE_KEY, value === LANDING_DEFAULT_VALUE ? null : value);
                }}
                testId={`${prefix}-landing-select`}
              />
              {landingHint.length > 0 && (
                <p
                  className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                  {...testIdProp(`${prefix}-landing-hint`)}
                >
                  {landingHint}
                </p>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {groups.length > 0 && (
        <Card id="notifications" testId={`${prefix}-notifications`}>
          <CardHeader
            title="Notifications"
            subtitle="Only what the apps you hold can send today. More arrive with the apps that send them."
          />
          <CardBody className="flex flex-col gap-16">
            {groups.map(([module, defs]) => (
              <div
                key={module}
                className="flex flex-col gap-12"
                {...testIdProp(`${prefix}-group-${module}`)}
              >
                {groups.length > 1 && (
                  <p className="font-sans text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
                    {config.vocab.moduleLabel(module)}
                  </p>
                )}
                {defs.map((definition) => {
                  const value =
                    definition.key in values ? values[definition.key] : definition.default;
                  const pending = savingKey === definition.key;
                  return (
                    <div
                      key={definition.key}
                      className="flex items-start justify-between gap-16 max-sm:flex-col max-sm:items-stretch"
                      {...testIdProp(`${prefix}-pref-${definition.key}`)}
                    >
                      <div className="min-w-0">
                        <p
                          id={`${prefix}-pref-${definition.key}-label`}
                          className="font-sans text-s14 font-w600 tracking-givmo text-text-strong"
                        >
                          {definition.label}
                        </p>
                        {definition.description !== null && (
                          <p className="mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                            {definition.description}
                          </p>
                        )}
                      </div>
                      {definition.kind === 'boolean' ? (
                        <Switch
                          appearance="saas"
                          checked={value === true}
                          disabled={pending}
                          onCheckedChange={(checked) => write(definition.key, checked)}
                          aria-labelledby={`${prefix}-pref-${definition.key}-label`}
                          testId={`${prefix}-pref-${definition.key}-control`}
                        />
                      ) : (
                        <Select
                          appearance="saas"
                          aria-label={definition.label}
                          options={definition.choices.map((choice) => ({
                            value: choice,
                            label: choiceLabel(choice),
                          }))}
                          value={typeof value === 'string' ? value : null}
                          disabled={pending}
                          onValueChange={(next) => next !== null && write(definition.key, next)}
                          className="w-[200px] shrink-0 max-sm:w-full"
                          testId={`${prefix}-pref-${definition.key}-control`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {update.isError && (
        <ErrorMessage testId={`${prefix}-save-error`}>
          We couldn’t save that. Nothing was changed — please try again.
        </ErrorMessage>
      )}
    </section>
  );
}
